import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { InvoiceGenerateSuccessTicket } from "../components/InvoiceGenerateSuccessTicket";
import { useAppState } from "../context/AppStateContext";
import { useToast } from "../context/ToastContext";
import {
  cancelInvoice,
  fetchEligibleLabourLines,
  fetchEligibleMaterialLines,
  fetchEligibleSubcontractorLines,
  fetchInvoiceItems,
  fetchInvoiceRegister,
  generateInvoice,
  type EligibleMaterialLine,
  type EligibleSubcontractorBeneficiary,
  type EligibleSubcontractorLine,
  type GenerateInvoiceLineInput,
  type InvoiceHeader,
  type InvoiceItem,
  type InvoiceType,
} from "../api/inventoryInvoicesApi";
import { fetchIssuePoItems, fetchIssuePoRegister } from "../api/mrrInventoryApi";
import type { Application } from "../types";
import { fetchLabourSuppliers, type LabourSupplierMaster } from "../api/labourSuppliersApi";
import {
  formatBillBlock,
  buildFormatBillFromBlock,
  buildFormatBillToBlock,
  loadInvoiceFormatSettings,
  totalGstRatePercent,
} from "../utils/invoiceFormatSettings";
import { downloadIndividualInvoicesZip, downloadInvoicePdf, mapWithConcurrency } from "../utils/issueInvoicePdf";
import { parseQuantityInput, sanitizeQtyFieldInput } from "../utils/qtyInput";
import { applyLabourBillFormatToLine } from "../utils/labourBillFormatLines";
import { timelineReferenceTitles } from "../utils/invoiceBillLineLabels";
import {
  applySubcontractorBillFormatToLine,
  milestonesForSubcontractorInvoicing,
} from "../utils/subcontractorBillFormatLines";
import { sortConstructionPhases } from "../utils/constructionTimeline";

type InvoiceViewMode = "generate" | "register";
type MaterialInvoiceMode = "individual" | "consolidated";
type SubcontractorBrowseMode = "milestone" | "timeline";
type LabourBrowseMode = "milestone" | "timeline";

function timelineReferenceFromPhaseIds(phaseIds: string[], titleById: Map<string, string>): string {
  return timelineReferenceTitles(phaseIds.map((id) => titleById.get(id) ?? "").filter(Boolean));
}

function formatWhen(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function fmtInr(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function invoiceTypeLabel(t: InvoiceType): string {
  if (t === "material") return "Material";
  if (t === "subcontractor") return "Subcontractor";
  return "Labour";
}

function buildBeneficiaryBillTo(app: Application): string {
  const addressParts = [
    app.sitePlot?.trim(),
    app.village?.trim(),
    app.mandal?.trim(),
    String(app.district ?? "").trim(),
  ].filter(Boolean);
  const address = [
    app.mobile.applicationId ? `Application ID: ${app.mobile.applicationId}` : "",
    addressParts.join(", "),
  ]
    .filter(Boolean)
    .join("\n");
  return formatBillBlock(app.mobile.beneficiaryName, address, "");
}

function materialLinesForApp(
  lines: EligibleMaterialLine[],
  appId: string,
  qtyOverrides: Record<string, string>,
): GenerateInvoiceLineInput[] {
  const out: GenerateInvoiceLineInput[] = [];
  for (const ln of lines) {
    if (ln.applicationId !== appId) continue;
    const rawQty = qtyOverrides[ln.lineKey];
    const qty = rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
    if (!(qty > 0)) continue;
    if (qty > ln.eligibleQty + 0.0001) {
      throw new Error(`Qty exceeds eligible for ${ln.beneficiaryName} — ${ln.lineLabel}.`);
    }
    out.push({
      applicationId: ln.applicationId,
      beneficiaryName: ln.beneficiaryName,
      beneficiaryMobileApplicationId: ln.beneficiaryMobileApplicationId,
      lineKey: ln.lineKey,
      lineLabel: ln.lineLabel,
      lineUnit: ln.lineUnit,
      qty,
      rate: ln.rate,
      hsnCode: ln.hsnCode,
      catalogStandardItemId: ln.catalogStandardItemId,
      sourcePoId: ln.poId,
      sourcePoItemId: ln.poItemId,
    });
  }
  return out;
}

function materialLinesToGenerateInput(
  lines: EligibleMaterialLine[],
  qtyOverrides: Record<string, string>,
): GenerateInvoiceLineInput[] {
  const out: GenerateInvoiceLineInput[] = [];
  for (const ln of lines) {
    const rawQty = qtyOverrides[ln.lineKey];
    const qty = rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
    if (!(qty > 0)) continue;
    if (qty > ln.eligibleQty + 0.0001) {
      throw new Error(`Qty exceeds eligible for ${ln.lineLabel}.`);
    }
    out.push({
      applicationId: ln.applicationId,
      beneficiaryName: ln.beneficiaryName,
      beneficiaryMobileApplicationId: ln.beneficiaryMobileApplicationId,
      lineKey: ln.lineKey,
      lineLabel: ln.lineLabel,
      lineUnit: ln.lineUnit,
      qty,
      rate: ln.rate,
      hsnCode: ln.hsnCode,
      catalogStandardItemId: ln.catalogStandardItemId,
      sourcePoId: ln.poId,
      sourcePoItemId: ln.poItemId,
    });
  }
  return out;
}

function subcontractorLinesForApp(
  lines: EligibleSubcontractorLine[],
  appId: string,
  qtyOverrides: Record<string, string>,
  rateOverrides: Record<string, string>,
): GenerateInvoiceLineInput[] {
  const out: GenerateInvoiceLineInput[] = [];
  for (const ln of lines) {
    if (ln.applicationId !== appId) continue;
    const rawQty = qtyOverrides[ln.lineKey];
    const qty = rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
    const rawRate = rateOverrides[ln.lineKey];
    const rate = rawRate != null && rawRate.trim() !== "" ? parseQuantityInput(rawRate) : ln.rate;
    if (!(qty > 0) || !(rate > 0)) continue;
    out.push({
      applicationId: ln.applicationId,
      beneficiaryName: ln.beneficiaryName,
      beneficiaryMobileApplicationId: ln.beneficiaryMobileApplicationId,
      lineKey: ln.lineKey,
      lineLabel: ln.lineLabel,
      lineUnit: ln.lineUnit,
      qty,
      rate,
      hsnCode: ln.hsnCode,
      sourceMilestoneTemplateItemId: ln.templateItemId,
    });
  }
  return out;
}

function subcontractorLinesToGenerateInput(
  lines: EligibleSubcontractorLine[],
  qtyOverrides: Record<string, string>,
  rateOverrides: Record<string, string>,
): GenerateInvoiceLineInput[] {
  const out: GenerateInvoiceLineInput[] = [];
  for (const ln of lines) {
    const rawQty = qtyOverrides[ln.lineKey];
    const qty = rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
    const rawRate = rateOverrides[ln.lineKey];
    const rate = rawRate != null && rawRate.trim() !== "" ? parseQuantityInput(rawRate) : ln.rate;
    if (!(qty > 0) || !(rate > 0)) continue;
    out.push({
      applicationId: ln.applicationId,
      beneficiaryName: ln.beneficiaryName,
      beneficiaryMobileApplicationId: ln.beneficiaryMobileApplicationId,
      lineKey: ln.lineKey,
      lineLabel: ln.lineLabel,
      lineUnit: ln.lineUnit,
      qty,
      rate,
      hsnCode: ln.hsnCode,
      sourceMilestoneTemplateItemId: ln.templateItemId,
    });
  }
  return out;
}

export function InvoiceRegisterPage() {
  const { applications, milestoneTemplates, subcontractorMasters, constructionTimelinePhases } = useAppState();
  const { showToast } = useToast();

  const [viewMode, setViewMode] = useState<InvoiceViewMode>("generate");
  const [invoiceType, setInvoiceType] = useState<InvoiceType>("material");
  const [invoiceApplicationIds, setInvoiceApplicationIds] = useState<string[]>([]);
  const [beneficiarySearch, setBeneficiarySearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [milestoneTemplateId, setMilestoneTemplateId] = useState("");
  const [materialPoId, setMaterialPoId] = useState("");
  const [materialInvoiceMode, setMaterialInvoiceMode] = useState<MaterialInvoiceMode>("consolidated");
  const [subcontractorBrowseMode, setSubcontractorBrowseMode] = useState<SubcontractorBrowseMode>("milestone");
  const [subcontractorTimelinePhaseIds, setSubcontractorTimelinePhaseIds] = useState<string[]>([]);
  const [subcontractorInvoiceMode, setSubcontractorInvoiceMode] = useState<MaterialInvoiceMode>("consolidated");
  const [subcontractorMilestoneSearch, setSubcontractorMilestoneSearch] = useState("");
  const [subcontractorBeneficiaries, setSubcontractorBeneficiaries] = useState<EligibleSubcontractorBeneficiary[]>([]);
  const [poSearch, setPoSearch] = useState("");
  const [labourSupplierId, setLabourSupplierId] = useState("");
  const [labourSuppliers, setLabourSuppliers] = useState<LabourSupplierMaster[]>([]);
  const [labourBrowseMode, setLabourBrowseMode] = useState<LabourBrowseMode>("milestone");
  const [labourTimelinePhaseIds, setLabourTimelinePhaseIds] = useState<string[]>([]);
  const [labourInvoiceMode, setLabourInvoiceMode] = useState<MaterialInvoiceMode>("consolidated");
  const [labourBeneficiaries, setLabourBeneficiaries] = useState<EligibleSubcontractorBeneficiary[]>([]);
  const [poList, setPoList] = useState<Awaited<ReturnType<typeof fetchIssuePoRegister>>>([]);
  const [materialLines, setMaterialLines] = useState<EligibleMaterialLine[]>([]);
  const [subcontractorLines, setSubcontractorLines] = useState<EligibleSubcontractorLine[]>([]);
  const [labourLines, setLabourLines] = useState<EligibleSubcontractorLine[]>([]);
  const [qtyOverrides, setQtyOverrides] = useState<Record<string, string>>({});
  const [rateOverrides, setRateOverrides] = useState<Record<string, string>>({});
  const [linesLoading, setLinesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState<{ phase: "invoices" | "pdfs"; done: number; total: number } | null>(
    null,
  );
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [generateSuccess, setGenerateSuccess] = useState<{ invoice: InvoiceHeader; items: InvoiceItem[] } | null>(
    null,
  );
  const [batchGenerateResult, setBatchGenerateResult] = useState<
    Array<{ header: InvoiceHeader; items: InvoiceItem[] }> | null
  >(null);
  const [batchZipBusy, setBatchZipBusy] = useState(false);

  const [registerRows, setRegisterRows] = useState<InvoiceHeader[]>([]);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSearch, setRegisterSearch] = useState("");
  const [registerTypeFilter, setRegisterTypeFilter] = useState<"all" | InvoiceType>("all");
  const [pdfDownloadingId, setPdfDownloadingId] = useState("");
  const [cancellingId, setCancellingId] = useState("");

  const appById = useMemo(() => new Map(applications.map((a) => [a.id, a])), [applications]);
  const subcontractorNameById = useMemo(
    () => new Map(subcontractorMasters.map((s) => [s.id, s.firmName])),
    [subcontractorMasters],
  );

  const eligibleApplications = useMemo(
    () =>
      applications
        .filter((a) => Boolean(a.subcontractor.subcontractorMasterId) && Boolean(a.plannedStartDate))
        .sort((a, b) => a.mobile.beneficiaryName.localeCompare(b.mobile.beneficiaryName)),
    [applications],
  );

  const districtOptions = useMemo(() => {
    const districts = [...new Set(eligibleApplications.map((a) => String(a.district).trim()).filter(Boolean))];
    districts.sort((a, b) => a.localeCompare(b));
    return districts;
  }, [eligibleApplications]);

  const filteredBeneficiaries = useMemo(() => {
    const q = beneficiarySearch.trim().toLowerCase();
    return eligibleApplications.filter((a) => {
      if (districtFilter !== "all" && a.district !== districtFilter) return false;
      if (!q) return true;
      return (
        a.mobile.beneficiaryName.toLowerCase().includes(q) ||
        a.mobile.applicationId.toLowerCase().includes(q)
      );
    });
  }, [eligibleApplications, beneficiarySearch, districtFilter]);

  const visibleBeneficiaryIds = useMemo(() => filteredBeneficiaries.map((a) => a.id), [filteredBeneficiaries]);
  const allVisibleSelected = useMemo(
    () => visibleBeneficiaryIds.length > 0 && visibleBeneficiaryIds.every((id) => invoiceApplicationIds.includes(id)),
    [visibleBeneficiaryIds, invoiceApplicationIds],
  );

  const subcontractorValidation = useMemo(() => {
    const lineAppIds = [...new Set(subcontractorLines.map((l) => l.applicationId).filter(Boolean))];
    const eligibleFromApi = subcontractorBeneficiaries.filter((b) => b.invoiceEligible);
    const subIds = new Set(
      (eligibleFromApi.length ? eligibleFromApi : subcontractorBeneficiaries)
        .map((b) => b.subcontractorMasterId ?? "")
        .filter(Boolean),
    );
    if (!lineAppIds.length && !subIds.size) return { ok: true, subcontractorId: "" };
    if (subIds.size !== 1) return { ok: false, subcontractorId: "" };
    return { ok: true, subcontractorId: [...subIds][0] };
  }, [subcontractorBeneficiaries, subcontractorLines]);

  const selectionMode =
    invoiceType === "material"
      ? materialInvoiceMode === "individual"
        ? "single"
        : "consolidated"
      : invoiceType === "subcontractor"
        ? subcontractorInvoiceMode === "individual"
          ? "single"
          : "consolidated"
        : invoiceType === "labour"
          ? labourInvoiceMode === "individual"
            ? "single"
            : "consolidated"
          : invoiceApplicationIds.length > 1
            ? "consolidated"
            : "single";

  const subcontractorMilestoneOptions = useMemo(
    () => milestonesForSubcontractorInvoicing(milestoneTemplates),
    [milestoneTemplates],
  );

  const sortedTimelinePhases = useMemo(
    () => sortConstructionPhases(constructionTimelinePhases),
    [constructionTimelinePhases],
  );

  const timelinePhaseTitleById = useMemo(
    () => new Map(sortedTimelinePhases.map((p) => [String(p.id), p.title])),
    [sortedTimelinePhases],
  );

  const filteredSubcontractorMilestones = useMemo(() => {
    const q = subcontractorMilestoneSearch.trim().toLowerCase();
    return subcontractorMilestoneOptions.filter((tpl) => {
      if (!q) return true;
      const timelineText = (tpl.linkedConstructionTimelinePhaseIds ?? [])
        .map((id) => timelinePhaseTitleById.get(String(id)) ?? "")
        .join(" ")
        .toLowerCase();
      return tpl.title.toLowerCase().includes(q) || timelineText.includes(q);
    });
  }, [subcontractorMilestoneOptions, subcontractorMilestoneSearch, timelinePhaseTitleById]);

  const selectedSubcontractorMilestone = useMemo(
    () => subcontractorMilestoneOptions.find((m) => m.id === milestoneTemplateId) ?? null,
    [subcontractorMilestoneOptions, milestoneTemplateId],
  );

  const subcontractorSelectionReady =
    (subcontractorBrowseMode === "milestone" && Boolean(milestoneTemplateId.trim())) ||
    (subcontractorBrowseMode === "timeline" && subcontractorTimelinePhaseIds.length > 0);

  const subcontractorBeneficiaryIds = useMemo(
    () => [...new Set(subcontractorLines.map((l) => l.applicationId).filter(Boolean))],
    [subcontractorLines],
  );

  const labourSelectionReady =
    (labourBrowseMode === "milestone" && Boolean(milestoneTemplateId.trim())) ||
    (labourBrowseMode === "timeline" && labourTimelinePhaseIds.length > 0);

  const labourBeneficiaryIds = useMemo(
    () => [...new Set(labourLines.map((l) => l.applicationId).filter(Boolean))],
    [labourLines],
  );

  const activePoList = useMemo(
    () => poList.filter((p) => (p.status ?? "active") === "active"),
    [poList],
  );

  const filteredPoList = useMemo(() => {
    const q = poSearch.trim().toLowerCase();
    return activePoList.filter((p) => {
      if (!q) return true;
      return (
        p.poNumber.toLowerCase().includes(q) ||
        p.milestoneTitle.toLowerCase().includes(q) ||
        p.subcontractorFirmName.toLowerCase().includes(q)
      );
    });
  }, [activePoList, poSearch]);

  const selectedMaterialPo = useMemo(
    () => activePoList.find((p) => p.id === materialPoId) ?? null,
    [activePoList, materialPoId],
  );

  const materialBeneficiaryIds = useMemo(
    () => [...new Set(materialLines.map((l) => l.applicationId).filter(Boolean))],
    [materialLines],
  );

  const loadRegister = useCallback(async () => {
    setRegisterLoading(true);
    try {
      const rows = await fetchInvoiceRegister({
        limit: 1000,
        search: registerSearch.trim() || undefined,
        invoiceType: registerTypeFilter === "all" ? undefined : registerTypeFilter,
      });
      setRegisterRows(rows);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not load invoice register.", "error");
    } finally {
      setRegisterLoading(false);
    }
  }, [registerSearch, registerTypeFilter, showToast]);

  useEffect(() => {
    if (viewMode === "register") void loadRegister();
  }, [viewMode, loadRegister]);

  useEffect(() => {
    void fetchLabourSuppliers()
      .then(setLabourSuppliers)
      .catch(() => setLabourSuppliers([]));
    void fetchIssuePoRegister({ limit: 1000, sort: "desc" })
      .then(setPoList)
      .catch(() => setPoList([]));
  }, []);

  useEffect(() => {
    setMaterialLines([]);
    setSubcontractorLines([]);
    setLabourLines([]);
    setQtyOverrides({});
    setRateOverrides({});
  }, [invoiceType]);

  useEffect(() => {
    if (invoiceType !== "material") return;
    setMaterialLines([]);
    setQtyOverrides({});
  }, [invoiceType, materialPoId]);

  useEffect(() => {
    if (invoiceType !== "subcontractor") return;
    setSubcontractorLines([]);
    setSubcontractorBeneficiaries([]);
    setQtyOverrides({});
    setRateOverrides({});
    setInvoiceApplicationIds([]);
  }, [invoiceType, milestoneTemplateId, subcontractorTimelinePhaseIds, subcontractorBrowseMode]);

  useEffect(() => {
    if (invoiceType !== "material") return;
    setMaterialPoId("");
    setMaterialInvoiceMode("consolidated");
    setPoSearch("");
    setInvoiceApplicationIds([]);
    setMilestoneTemplateId("");
    setSubcontractorBrowseMode("milestone");
    setSubcontractorTimelinePhaseIds([]);
    setSubcontractorInvoiceMode("consolidated");
    setSubcontractorMilestoneSearch("");
    setSubcontractorBeneficiaries([]);
    setLabourTimelinePhaseIds([]);
    setLabourBrowseMode("milestone");
    setLabourInvoiceMode("consolidated");
    setLabourBeneficiaries([]);
    setLabourSupplierId("");
  }, [invoiceType]);

  useEffect(() => {
    if (invoiceType !== "labour") return;
    setLabourLines([]);
    setLabourBeneficiaries([]);
    setQtyOverrides({});
    setRateOverrides({});
    setInvoiceApplicationIds([]);
  }, [invoiceType, labourBrowseMode, milestoneTemplateId, labourTimelinePhaseIds]);

  useEffect(() => {
    if (invoiceType !== "material" || !materialPoId) {
      if (invoiceType === "material") setInvoiceApplicationIds([]);
      return;
    }
    const po = activePoList.find((p) => p.id === materialPoId);
    if (po) setMilestoneTemplateId(po.milestoneTemplateId);
    void fetchIssuePoItems(materialPoId)
      .then((items) => {
        setInvoiceApplicationIds([...new Set(items.map((i) => i.applicationId).filter(Boolean))]);
      })
      .catch(() => setInvoiceApplicationIds([]));
  }, [invoiceType, materialPoId, activePoList]);
  useEffect(() => {
    if (invoiceType !== "material" || !materialPoId) return;
    let cancelled = false;
    setLinesLoading(true);
    void fetchEligibleMaterialLines({
      poId: materialPoId,
    })
      .then((data) => {
        if (!cancelled) setMaterialLines(data.lines.filter((l) => l.eligibleQty > 0.0000001));
      })
      .catch((e) => {
        if (!cancelled) {
          setMaterialLines([]);
          showToast(e instanceof Error ? e.message : "Could not load material lines.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceType, materialPoId, showToast]);

  useEffect(() => {
    if (invoiceType !== "subcontractor") return;
    const milestoneReady = subcontractorBrowseMode === "milestone" && milestoneTemplateId.trim();
    const timelineReady =
      subcontractorBrowseMode === "timeline" && subcontractorTimelinePhaseIds.length > 0;
    if (!milestoneReady && !timelineReady) return;

    let cancelled = false;
    setLinesLoading(true);
    const fmt = loadInvoiceFormatSettings();
    void fetchEligibleSubcontractorLines(
      milestoneReady
        ? { sourceMode: "milestone", milestoneTemplateId: milestoneTemplateId.trim() }
        : { sourceMode: "timeline", timelinePhaseIds: subcontractorTimelinePhaseIds },
    )
      .then((data) => {
        if (cancelled) return;
        setSubcontractorBeneficiaries(data.beneficiaries ?? []);
        setInvoiceApplicationIds(data.applicationIds ?? []);
        setSubcontractorLines(data.lines.map((ln) => applySubcontractorBillFormatToLine(ln, fmt)));
      })
      .catch((e) => {
        if (!cancelled) {
          setSubcontractorLines([]);
          setSubcontractorBeneficiaries([]);
          setInvoiceApplicationIds([]);
          showToast(e instanceof Error ? e.message : "Could not load subcontractor lines.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    invoiceType,
    subcontractorBrowseMode,
    milestoneTemplateId,
    subcontractorTimelinePhaseIds,
    showToast,
  ]);

  useEffect(() => {
    if (invoiceType !== "labour") return;
    const milestoneReady = labourBrowseMode === "milestone" && milestoneTemplateId.trim();
    const timelineReady = labourBrowseMode === "timeline" && labourTimelinePhaseIds.length > 0;
    if (!milestoneReady && !timelineReady) return;

    let cancelled = false;
    setLinesLoading(true);
    const fmt = loadInvoiceFormatSettings();
    void fetchEligibleLabourLines(
      milestoneReady
        ? { sourceMode: "milestone", milestoneTemplateId: milestoneTemplateId.trim() }
        : { sourceMode: "timeline", timelinePhaseIds: labourTimelinePhaseIds },
    )
      .then((data) => {
        if (cancelled) return;
        setLabourBeneficiaries(data.beneficiaries ?? []);
        setInvoiceApplicationIds(data.applicationIds ?? []);
        setLabourLines(data.lines.map((ln) => applyLabourBillFormatToLine(ln, fmt)));
      })
      .catch((e) => {
        if (!cancelled) {
          setLabourLines([]);
          setLabourBeneficiaries([]);
          setInvoiceApplicationIds([]);
          showToast(e instanceof Error ? e.message : "Could not load labour lines.", "error");
        }
      })
      .finally(() => {
        if (!cancelled) setLinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceType, labourBrowseMode, milestoneTemplateId, labourTimelinePhaseIds, showToast]);

  const labourSupplierOptions = useMemo(
    () => [
      { value: "", label: "Select labour supplier…" },
      ...labourSuppliers.map((s) => ({ value: s.id, label: s.firmName })),
    ],
    [labourSuppliers],
  );

  function buildCompanyBillTo() {
    return buildFormatBillToBlock(invoiceType);
  }

  function buildFormatBillFrom() {
    return buildFormatBillFromBlock(invoiceType);
  }

  async function runGenerate() {
    const fmt = loadInvoiceFormatSettings();
    const gstRatePercent = totalGstRatePercent(fmt, invoiceType);
    /** @type {GenerateInvoiceLineInput[]} */
    const lines: GenerateInvoiceLineInput[] = [];

    if (invoiceType === "material") {
      if (!materialPoId || !selectedMaterialPo) {
        showToast("Select a purchase order.", "error");
        return;
      }
      if (!materialLines.length) {
        showToast(
          selectedMaterialPo
            ? "No invoiceable lines on this PO — all lines may already be invoiced, or the PO has no material rows."
            : "Select a purchase order.",
          "error",
        );
        return;
      }
      const sub = subcontractorMasters.find((s) => s.id === selectedMaterialPo.subcontractorMasterId);
      const billFrom = buildFormatBillFrom();
      const milestoneId = selectedMaterialPo.milestoneTemplateId || materialLines[0]?.milestoneTemplateId;
      setGenerating(true);
      setGenerateProgress(null);
      setBatchGenerateResult(null);
      try {
        if (materialInvoiceMode === "individual") {
          const jobs = materialBeneficiaryIds
            .map((appId) => ({
              appId,
              lines: materialLinesForApp(materialLines, appId, qtyOverrides),
            }))
            .filter((j) => j.lines.length > 0);

          if (!jobs.length) {
            showToast("Enter quantity on at least one line.", "error");
            return;
          }

          setGenerateProgress({ phase: "invoices", done: 0, total: jobs.length });
          let invoicesDone = 0;
          const generated = await mapWithConcurrency(jobs, 6, async ({ appId, lines }) => {
            const app = appById.get(appId);
            const billTo = app ? buildBeneficiaryBillTo(app) : buildCompanyBillTo();
            const result = await generateInvoice({
              invoiceType: "material",
              applicationIds: [appId],
              selectionMode: "single",
              partyKind: "subcontractor",
              partyMasterId: selectedMaterialPo.subcontractorMasterId,
              billFromText: billFrom,
              billToText: billTo,
              sourcePoId: materialPoId,
              sourceMilestoneTemplateId: milestoneId,
              notes: invoiceNotes.trim() || undefined,
              gstRatePercent,
              lines,
            });
            invoicesDone += 1;
            setGenerateProgress({ phase: "invoices", done: invoicesDone, total: jobs.length });
            return { header: result.invoice, items: result.items };
          });

          setBatchGenerateResult(generated);
          setGenerateProgress({ phase: "pdfs", done: 0, total: generated.length });
          showToast(`Generated ${generated.length} invoice(s). Building PDF ZIP…`, "success");
          try {
            await downloadIndividualInvoicesZip(generated, {
              applications,
              zipBasename: `Invoices_${selectedMaterialPo.poNumber.replace(/[^\w.-]+/g, "_")}`,
              pdfConcurrency: 6,
              onProgress: (done, total) => setGenerateProgress({ phase: "pdfs", done, total }),
            });
            showToast(`ZIP downloaded with ${generated.length} invoice PDF(s).`, "success");
          } catch (zipErr) {
            showToast(
              zipErr instanceof Error ? zipErr.message : "Invoices saved but ZIP download failed.",
              "error",
            );
          }
        } else {
          const lines = materialLinesToGenerateInput(materialLines, qtyOverrides);
          if (!lines.length) {
            showToast("Enter quantity on at least one line.", "error");
            return;
          }
          const result = await generateInvoice({
            invoiceType: "material",
            applicationIds: invoiceApplicationIds,
            selectionMode: "consolidated",
            partyKind: "subcontractor",
            partyMasterId: selectedMaterialPo.subcontractorMasterId,
            billFromText: billFrom,
            billToText: buildCompanyBillTo(),
            sourcePoId: materialPoId,
            sourceMilestoneTemplateId: milestoneId,
            notes: invoiceNotes.trim() || undefined,
            gstRatePercent,
            lines,
          });
          setGenerateSuccess(result);
          showToast(`Invoice ${result.invoice.invoiceNumber} generated.`, "success");
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not generate invoice.", "error");
      } finally {
        setGenerating(false);
        setGenerateProgress(null);
      }
      return;
    }

    if (invoiceType === "subcontractor") {
      const milestoneReady = subcontractorBrowseMode === "milestone" && milestoneTemplateId.trim();
      const timelineReady =
        subcontractorBrowseMode === "timeline" && subcontractorTimelinePhaseIds.length > 0;
      if (!milestoneReady && !timelineReady) {
        showToast("Select a milestone or at least one timeline level.", "error");
        return;
      }
      if (!subcontractorLines.length) {
        showToast("No invoiceable subcontractor bill lines for this milestone — check completion and invoice format rates.", "error");
        return;
      }
      if (!subcontractorValidation.ok) {
        showToast("Eligible beneficiaries must share one subcontractor.", "error");
        return;
      }
      const billFrom = buildFormatBillFrom();
      const milestoneTitle = selectedSubcontractorMilestone?.title ?? milestoneTemplateId;
      const sourceMilestoneTitle =
        subcontractorBrowseMode === "timeline"
          ? timelineReferenceFromPhaseIds(subcontractorTimelinePhaseIds, timelinePhaseTitleById) || undefined
          : selectedSubcontractorMilestone?.title?.trim() || undefined;
      setGenerating(true);
      setGenerateProgress(null);
      setBatchGenerateResult(null);
      try {
        if (subcontractorInvoiceMode === "individual") {
          const jobs = subcontractorBeneficiaryIds
            .map((appId) => ({
              appId,
              lines: subcontractorLinesForApp(subcontractorLines, appId, qtyOverrides, rateOverrides),
            }))
            .filter((j) => j.lines.length > 0);

          if (!jobs.length) {
            showToast("Configure rate/qty in Invoice format and ensure at least one eligible line.", "error");
            return;
          }

          setGenerateProgress({ phase: "invoices", done: 0, total: jobs.length });
          let invoicesDone = 0;
          const generated = await mapWithConcurrency(jobs, 6, async ({ appId, lines }) => {
            const app = appById.get(appId);
            const billTo = app ? buildBeneficiaryBillTo(app) : buildCompanyBillTo();
            const result = await generateInvoice({
              invoiceType: "subcontractor",
              applicationIds: [appId],
              selectionMode: "single",
              partyKind: "subcontractor",
              partyMasterId: subcontractorValidation.subcontractorId,
              billFromText: billFrom,
              billToText: billTo,
              sourceMilestoneTemplateId:
                subcontractorBrowseMode === "milestone" ? milestoneTemplateId.trim() : undefined,
              sourceMilestoneTitle,
              notes: invoiceNotes.trim() || undefined,
              gstRatePercent,
              lines,
            });
            invoicesDone += 1;
            setGenerateProgress({ phase: "invoices", done: invoicesDone, total: jobs.length });
            return { header: result.invoice, items: result.items };
          });

          setBatchGenerateResult(generated);
          setGenerateProgress({ phase: "pdfs", done: 0, total: generated.length });
          showToast(`Generated ${generated.length} invoice(s). Building PDF ZIP…`, "success");
          try {
            await downloadIndividualInvoicesZip(generated, {
              applications,
              zipBasename: `Subcontractor_Invoices_${milestoneTitle.replace(/[^\w.-]+/g, "_").slice(0, 40)}`,
              pdfConcurrency: 6,
              onProgress: (done, total) => setGenerateProgress({ phase: "pdfs", done, total }),
            });
            showToast(`ZIP downloaded with ${generated.length} invoice PDF(s).`, "success");
          } catch (zipErr) {
            showToast(
              zipErr instanceof Error ? zipErr.message : "Invoices saved but ZIP download failed.",
              "error",
            );
          }
        } else {
          const lines = subcontractorLinesToGenerateInput(subcontractorLines, qtyOverrides, rateOverrides);
          if (!lines.length) {
            showToast("Configure rate/qty in Invoice format and ensure at least one eligible line.", "error");
            return;
          }
          const result = await generateInvoice({
            invoiceType: "subcontractor",
            applicationIds: subcontractorBeneficiaryIds,
            selectionMode: "consolidated",
            partyKind: "subcontractor",
            partyMasterId: subcontractorValidation.subcontractorId,
            billFromText: billFrom,
            billToText: buildCompanyBillTo(),
            sourceMilestoneTemplateId:
              subcontractorBrowseMode === "milestone" ? milestoneTemplateId.trim() : undefined,
            sourceMilestoneTitle,
            notes: invoiceNotes.trim() || undefined,
            gstRatePercent,
            lines,
          });
          setGenerateSuccess(result);
          showToast(`Invoice ${result.invoice.invoiceNumber} generated.`, "success");
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not generate invoice.", "error");
      } finally {
        setGenerating(false);
        setGenerateProgress(null);
      }
      return;
    }

    if (invoiceType === "labour") {
      const milestoneReady = labourBrowseMode === "milestone" && milestoneTemplateId.trim();
      const timelineReady = labourBrowseMode === "timeline" && labourTimelinePhaseIds.length > 0;
      if (!milestoneReady && !timelineReady) {
        showToast("Select a milestone or at least one timeline level.", "error");
        return;
      }
      if (!labourSupplierId.trim()) {
        showToast("Select a labour supplier.", "error");
        return;
      }
      const supplier = labourSuppliers.find((s) => s.id === labourSupplierId);
      if (!supplier) {
        showToast("Labour supplier not found.", "error");
        return;
      }
      if (!labourLines.length) {
        showToast(
          "No invoiceable labour bill lines — check completion and invoice format rates.",
          "error",
        );
        return;
      }
      const billFrom = buildFormatBillFrom();
      const labourMilestoneTitle = selectedSubcontractorMilestone?.title?.trim() || undefined;
      const sourceMilestoneTitle =
        labourBrowseMode === "timeline"
          ? timelineReferenceFromPhaseIds(labourTimelinePhaseIds, timelinePhaseTitleById) || undefined
          : labourMilestoneTitle;
      const zipLabel =
        labourBrowseMode === "milestone"
          ? selectedSubcontractorMilestone?.title ?? "milestone"
          : labourTimelinePhaseIds.length === 1
            ? timelinePhaseTitleById.get(labourTimelinePhaseIds[0]) ?? "timeline"
            : `${labourTimelinePhaseIds.length} timeline levels`;
      setGenerating(true);
      setGenerateProgress(null);
      setBatchGenerateResult(null);
      try {
        if (labourInvoiceMode === "individual") {
          const jobs = labourBeneficiaryIds
            .map((appId) => ({
              appId,
              lines: subcontractorLinesForApp(labourLines, appId, qtyOverrides, rateOverrides),
            }))
            .filter((j) => j.lines.length > 0);

          if (!jobs.length) {
            showToast("Configure rate/qty in Invoice format and ensure at least one eligible line.", "error");
            return;
          }

          setGenerateProgress({ phase: "invoices", done: 0, total: jobs.length });
          let invoicesDone = 0;
          const generated = await mapWithConcurrency(jobs, 6, async ({ appId, lines: jobLines }) => {
            const app = appById.get(appId);
            const billTo = app ? buildBeneficiaryBillTo(app) : buildCompanyBillTo();
            const result = await generateInvoice({
              invoiceType: "labour",
              applicationIds: [appId],
              selectionMode: "single",
              partyKind: "labour_supplier",
              partyMasterId: supplier.id,
              billFromText: billFrom,
              billToText: billTo,
              sourceMilestoneTemplateId:
                labourBrowseMode === "milestone" ? milestoneTemplateId.trim() : undefined,
              sourceMilestoneTitle,
              notes: invoiceNotes.trim() || undefined,
              gstRatePercent,
              lines: jobLines,
            });
            invoicesDone += 1;
            setGenerateProgress({ phase: "invoices", done: invoicesDone, total: jobs.length });
            return { header: result.invoice, items: result.items };
          });

          setBatchGenerateResult(generated);
          setGenerateProgress({ phase: "pdfs", done: 0, total: generated.length });
          showToast(`Generated ${generated.length} invoice(s). Building PDF ZIP…`, "success");
          try {
            await downloadIndividualInvoicesZip(generated, {
              applications,
              zipBasename: `Labour_Invoices_${zipLabel.replace(/[^\w.-]+/g, "_").slice(0, 40)}`,
              pdfConcurrency: 6,
              onProgress: (done, total) => setGenerateProgress({ phase: "pdfs", done, total }),
            });
            showToast(`ZIP downloaded with ${generated.length} invoice PDF(s).`, "success");
          } catch (zipErr) {
            showToast(
              zipErr instanceof Error ? zipErr.message : "Invoices saved but ZIP download failed.",
              "error",
            );
          }
        } else {
          const jobLines = subcontractorLinesToGenerateInput(labourLines, qtyOverrides, rateOverrides);
          if (!jobLines.length) {
            showToast("Configure rate/qty in Invoice format and ensure at least one eligible line.", "error");
            return;
          }
          const result = await generateInvoice({
            invoiceType: "labour",
            applicationIds: invoiceApplicationIds,
            selectionMode: "consolidated",
            partyKind: "labour_supplier",
            partyMasterId: supplier.id,
            billFromText: billFrom,
            billToText: buildCompanyBillTo(),
            sourceMilestoneTemplateId:
              labourBrowseMode === "milestone" ? milestoneTemplateId.trim() : undefined,
            sourceMilestoneTitle,
            notes: invoiceNotes.trim() || undefined,
            gstRatePercent,
            lines: jobLines,
          });
          setGenerateSuccess(result);
          showToast(`Invoice ${result.invoice.invoiceNumber} generated.`, "success");
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Could not generate invoice.", "error");
      } finally {
        setGenerating(false);
        setGenerateProgress(null);
      }
      return;
    }
  }

  async function handleDownloadRegisterPdf(row: InvoiceHeader) {
    setPdfDownloadingId(row.id);
    try {
      const items = await fetchInvoiceItems(row.id);
      await downloadInvoicePdf(row, items, { applications });
      showToast(`PDF downloaded (${row.invoiceNumber}).`, "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not download PDF.", "error");
    } finally {
      setPdfDownloadingId("");
    }
  }

  async function handleDownloadBatchZip() {
    if (!batchGenerateResult?.length) return;
    setBatchZipBusy(true);
    setGenerateProgress({ phase: "pdfs", done: 0, total: batchGenerateResult.length });
    try {
      await downloadIndividualInvoicesZip(batchGenerateResult, {
        applications,
        zipBasename: selectedMaterialPo
          ? `Invoices_${selectedMaterialPo.poNumber.replace(/[^\w.-]+/g, "_")}`
          : undefined,
        pdfConcurrency: 6,
        onProgress: (done, total) => setGenerateProgress({ phase: "pdfs", done, total }),
      });
      showToast(`ZIP downloaded with ${batchGenerateResult.length} invoice PDF(s).`, "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not download ZIP.", "error");
    } finally {
      setBatchZipBusy(false);
      setGenerateProgress(null);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Invoice" }, { label: "Invoice register" }]}
        title="Invoice register"
        subtitle="Generate material, subcontractor, or labour supplier invoices — single beneficiary or consolidated."
        actions={
          <div className="toolbar-row" style={{ margin: 0 }}>
            {viewMode === "register" ? (
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => void loadRegister()}>
                Refresh
              </button>
            ) : null}
          </div>
        }
      />

      {batchGenerateResult && batchGenerateResult.length > 0 ? (
        <div className="card card--elevated" style={{ marginBottom: "1rem" }}>
          <p style={{ margin: "0 0 0.5rem" }}>
            <strong>{batchGenerateResult.length} individual invoices</strong> generated for PO{" "}
            {selectedMaterialPo?.poNumber ?? materialPoId}.
          </p>
          <p className="text-muted" style={{ margin: "0 0 0.75rem", fontSize: "0.88rem" }}>
            PDFs in the ZIP are named <strong>ApplicationID_BeneficiaryName.pdf</strong> (e.g.{" "}
            APP-NK-00019_Ayodhyanallapothula.pdf).
          </p>
          <p className="text-muted" style={{ margin: "0 0 0.75rem", fontSize: "0.88rem" }}>
            {batchGenerateResult.map(({ header }) => header.invoiceNumber).join(", ")}
          </p>
          <div className="toolbar-row" style={{ margin: 0 }}>
            <button
              type="button"
              className="btn btn--primary btn--tg btn--sm"
              disabled={batchZipBusy}
              onClick={() => void handleDownloadBatchZip()}
            >
              {batchZipBusy ? "Preparing ZIP…" : "Download ZIP again"}
            </button>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              onClick={() => {
                setBatchGenerateResult(null);
                setViewMode("register");
                void loadRegister();
              }}
            >
              View in register
            </button>
            <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setBatchGenerateResult(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {generateSuccess ? (
        <InvoiceGenerateSuccessTicket
          invoice={generateSuccess.invoice}
          items={generateSuccess.items}
          applications={applications}
          onClose={() => setGenerateSuccess(null)}
          onCancel={async () => {
            await cancelInvoice(generateSuccess.invoice.id);
            setGenerateSuccess(null);
            await loadRegister();
            showToast(`Invoice ${generateSuccess.invoice.invoiceNumber} cancelled.`, "success");
          }}
        />
      ) : null}

      <div className="issue-mode-toggle-wrap">
        <div className="issue-mode-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "generate"}
            className={`issue-mode-toggle__btn ${viewMode === "generate" ? "is-active" : ""}`}
            onClick={() => setViewMode("generate")}
          >
            Generate Invoice
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "register"}
            className={`issue-mode-toggle__btn ${viewMode === "register" ? "is-active" : ""}`}
            onClick={() => setViewMode("register")}
          >
            Invoice Register
          </button>
        </div>
      </div>

      {viewMode === "generate" ? (
        <div className="issue-po-flow">
          <section className="issue-po-flow__section">
            <span className="inv-outward-issue__label">Step 1 — Invoice type</span>
            <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
              {(["material", "subcontractor", "labour"] as InvoiceType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`btn btn--sm ${invoiceType === t ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                  onClick={() => setInvoiceType(t)}
                >
                  {invoiceTypeLabel(t)}
                </button>
              ))}
            </div>
          </section>

          <section className="issue-po-flow__section">
            {invoiceType === "material" ? (
              <>
                <span className="inv-outward-issue__label">Step 2 — Select purchase order</span>
                <div className="goods-receipts-filters__grid" style={{ marginTop: "0.5rem" }}>
                  <label className="goods-receipts-filters__field">
                    <span className="goods-receipts-filters__label">Search PO</span>
                    <input
                      className="form-control"
                      value={poSearch}
                      onChange={(e) => setPoSearch(e.target.value)}
                      placeholder="PO number / milestone / subcontractor"
                    />
                  </label>
                </div>
                <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Selected PO:{" "}
                  <strong>{selectedMaterialPo ? selectedMaterialPo.poNumber : "—"}</strong>
                  {selectedMaterialPo ? (
                    <>
                      {" "}
                      · {selectedMaterialPo.beneficiaryCount} beneficiary(ies) · {selectedMaterialPo.milestoneTitle}
                    </>
                  ) : null}
                </p>
                <div className="issue-po-beneficiary-table-wrap">
                  <table className="data-table issue-po-beneficiary-table">
                    <thead>
                      <tr>
                        <th style={{ width: "2.5rem" }} />
                        <th>PO number</th>
                        <th>Milestone</th>
                        <th>Subcontractor</th>
                        <th>Beneficiaries</th>
                        <th>PO date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPoList.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-muted">
                            No active purchase orders found.
                          </td>
                        </tr>
                      ) : (
                        filteredPoList.map((po) => (
                          <tr key={po.id}>
                            <td>
                              <input
                                type="radio"
                                name="material-po-select"
                                checked={materialPoId === po.id}
                                onChange={() => setMaterialPoId(po.id)}
                                aria-label={`Select PO ${po.poNumber}`}
                              />
                            </td>
                            <td className="mono">{po.poNumber}</td>
                            <td>{po.milestoneTitle}</td>
                            <td>{po.subcontractorFirmName}</td>
                            <td>{po.beneficiaryCount}</td>
                            <td>{po.poDate || po.createdAt.slice(0, 10)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : invoiceType === "subcontractor" ? (
              <>
                <span className="inv-outward-issue__label">Step 2 — Select milestone or timeline level</span>
                <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    className={`btn btn--sm ${subcontractorBrowseMode === "milestone" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                    onClick={() => {
                      setSubcontractorBrowseMode("milestone");
                      setSubcontractorTimelinePhaseIds([]);
                      setMilestoneTemplateId("");
                    }}
                  >
                    By milestone
                  </button>
                  <button
                    type="button"
                    className={`btn btn--sm ${subcontractorBrowseMode === "timeline" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                    onClick={() => {
                      setSubcontractorBrowseMode("timeline");
                      setMilestoneTemplateId("");
                      setSubcontractorTimelinePhaseIds([]);
                    }}
                  >
                    By timeline level
                  </button>
                </div>
                {subcontractorBrowseMode === "milestone" ? (
                  <>
                    <div className="goods-receipts-filters__grid" style={{ marginTop: "0.5rem" }}>
                      <label className="goods-receipts-filters__field">
                        <span className="goods-receipts-filters__label">Search milestone</span>
                        <input
                          className="form-control"
                          value={subcontractorMilestoneSearch}
                          onChange={(e) => setSubcontractorMilestoneSearch(e.target.value)}
                          placeholder="Milestone / timeline level"
                        />
                      </label>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                      Selected: <strong>{selectedSubcontractorMilestone?.title ?? "—"}</strong>
                    </p>
                    <div className="issue-po-beneficiary-table-wrap">
                      <table className="data-table issue-po-beneficiary-table">
                        <thead>
                          <tr>
                            <th style={{ width: "2.5rem" }} />
                            <th>Milestone</th>
                            <th>Timeline levels</th>
                            <th>Bill items</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubcontractorMilestones.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-muted">
                                No milestones in master yet. Add milestones under Milestone master and set rates in
                                Invoice format.
                              </td>
                            </tr>
                          ) : (
                            filteredSubcontractorMilestones.map((tpl) => {
                              const billCount =
                                tpl.items.filter((i) => i.kind === "subcontractor_bill").length || 1;
                              const timelineText = (tpl.linkedConstructionTimelinePhaseIds ?? [])
                                .map((id) => timelinePhaseTitleById.get(String(id)) ?? "")
                                .filter(Boolean)
                                .join(", ");
                              return (
                                <tr key={tpl.id}>
                                  <td>
                                    <input
                                      type="radio"
                                      name="subcontractor-milestone-select"
                                      checked={milestoneTemplateId === tpl.id}
                                      onChange={() => setMilestoneTemplateId(tpl.id)}
                                      aria-label={`Select milestone ${tpl.title}`}
                                    />
                                  </td>
                                  <td>{tpl.title}</td>
                                  <td className="text-muted">{timelineText || "—"}</td>
                                  <td>{billCount}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="toolbar-row" style={{ marginTop: "0.35rem" }}>
                      <button
                        type="button"
                        className="btn btn--sm btn--tg-secondary"
                        onClick={() =>
                          setSubcontractorTimelinePhaseIds(sortedTimelinePhases.map((p) => String(p.id)))
                        }
                      >
                        Select all timeline levels
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--tg-secondary"
                        onClick={() => setSubcontractorTimelinePhaseIds([])}
                      >
                        Clear all
                      </button>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
                      Selected: <strong>{subcontractorTimelinePhaseIds.length}</strong> timeline level(s). Beneficiaries
                      must have <strong>Approved</strong> status on all selected levels.
                    </p>
                    <div className="issue-po-beneficiary-table-wrap">
                      <table className="data-table issue-po-beneficiary-table">
                        <thead>
                          <tr>
                            <th style={{ width: "2.5rem" }}>
                              <input
                                type="checkbox"
                                checked={
                                  sortedTimelinePhases.length > 0 &&
                                  sortedTimelinePhases.every((p) =>
                                    subcontractorTimelinePhaseIds.includes(String(p.id)),
                                  )
                                }
                                onChange={(e) =>
                                  setSubcontractorTimelinePhaseIds(
                                    e.target.checked ? sortedTimelinePhases.map((p) => String(p.id)) : [],
                                  )
                                }
                              />
                            </th>
                            <th>Timeline level</th>
                            <th>Planned days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTimelinePhases.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-muted">
                                No timeline levels in master yet.
                              </td>
                            </tr>
                          ) : (
                            sortedTimelinePhases.map((phase) => {
                              const phaseId = String(phase.id);
                              return (
                                <tr key={phase.id}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={subcontractorTimelinePhaseIds.includes(phaseId)}
                                      onChange={() =>
                                        setSubcontractorTimelinePhaseIds((prev) =>
                                          prev.includes(phaseId)
                                            ? prev.filter((id) => id !== phaseId)
                                            : [...prev, phaseId],
                                        )
                                      }
                                      aria-label={`Select timeline ${phase.title}`}
                                    />
                                  </td>
                                  <td>{phase.title}</td>
                                  <td className="mono">~{phase.approxDays} d</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {(subcontractorBrowseMode === "milestone" && milestoneTemplateId) ||
                (subcontractorBrowseMode === "timeline" && subcontractorTimelinePhaseIds.length > 0) ? (
                  <div style={{ marginTop: "1rem" }}>
                    <span className="inv-outward-issue__label">Completed beneficiaries</span>
                    {linesLoading ? (
                      <p className="text-muted" style={{ marginTop: "0.35rem" }}>Loading beneficiaries…</p>
                    ) : subcontractorBeneficiaries.length === 0 ? (
                      <p className="text-muted" style={{ marginTop: "0.35rem" }}>
                        No beneficiaries have completed{" "}
                        {subcontractorBrowseMode === "milestone"
                          ? selectedSubcontractorMilestone?.title ?? "this milestone"
                          : "all selected timeline levels (office-approved)"}
                        .
                      </p>
                    ) : (
                      <>
                        <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
                          Completed: <strong>{subcontractorBeneficiaries.length}</strong> · Ready to invoice:{" "}
                          <strong>{subcontractorBeneficiaries.filter((b) => b.invoiceEligible).length}</strong>
                        </p>
                        <div className="issue-po-beneficiary-table-wrap">
                          <table className="data-table issue-po-beneficiary-table">
                            <thead>
                              <tr>
                                <th>Application ID</th>
                                <th>Name</th>
                                <th>Subcontractor</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subcontractorBeneficiaries.map((b) => (
                                <tr key={b.applicationId}>
                                  <td className="mono">{b.beneficiaryMobileApplicationId || b.applicationId}</td>
                                  <td>{b.beneficiaryName}</td>
                                  <td>{subcontractorNameById.get(b.subcontractorMasterId || "") || "—"}</td>
                                  <td>
                                    {b.invoiceEligible ? (
                                      <span style={{ color: "#15803d" }}>Ready to invoice</span>
                                    ) : b.skipReason ? (
                                      <span style={{ color: "#dc2626" }}>{b.skipReason}</span>
                                    ) : (
                                      <span className="text-muted">Already invoiced</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            ) : invoiceType === "labour" ? (
              <>
                <span className="inv-outward-issue__label">Step 2 — Select milestone or timeline level</span>
                <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    className={`btn btn--sm ${labourBrowseMode === "milestone" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                    onClick={() => {
                      setLabourBrowseMode("milestone");
                      setLabourTimelinePhaseIds([]);
                      setMilestoneTemplateId("");
                    }}
                  >
                    By milestone
                  </button>
                  <button
                    type="button"
                    className={`btn btn--sm ${labourBrowseMode === "timeline" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                    onClick={() => {
                      setLabourBrowseMode("timeline");
                      setMilestoneTemplateId("");
                      setLabourTimelinePhaseIds([]);
                    }}
                  >
                    By timeline level
                  </button>
                </div>
                {labourBrowseMode === "milestone" ? (
                  <>
                    <div className="goods-receipts-filters__grid" style={{ marginTop: "0.5rem" }}>
                      <label className="goods-receipts-filters__field">
                        <span className="goods-receipts-filters__label">Search milestone</span>
                        <input
                          className="form-control"
                          value={subcontractorMilestoneSearch}
                          onChange={(e) => setSubcontractorMilestoneSearch(e.target.value)}
                          placeholder="Milestone / timeline level"
                        />
                      </label>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                      Selected: <strong>{selectedSubcontractorMilestone?.title ?? "—"}</strong>
                    </p>
                    <div className="issue-po-beneficiary-table-wrap">
                      <table className="data-table issue-po-beneficiary-table">
                        <thead>
                          <tr>
                            <th style={{ width: "2.5rem" }} />
                            <th>Milestone</th>
                            <th>Timeline levels</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSubcontractorMilestones.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-muted">
                                No milestones in master yet.
                              </td>
                            </tr>
                          ) : (
                            filteredSubcontractorMilestones.map((tpl) => {
                              const timelineText = (tpl.linkedConstructionTimelinePhaseIds ?? [])
                                .map((id) => timelinePhaseTitleById.get(String(id)) ?? "")
                                .filter(Boolean)
                                .join(", ");
                              return (
                                <tr key={tpl.id}>
                                  <td>
                                    <input
                                      type="radio"
                                      name="labour-milestone-select"
                                      checked={milestoneTemplateId === tpl.id}
                                      onChange={() => setMilestoneTemplateId(tpl.id)}
                                      aria-label={`Select milestone ${tpl.title}`}
                                    />
                                  </td>
                                  <td>{tpl.title}</td>
                                  <td className="text-muted">{timelineText || "—"}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="toolbar-row" style={{ marginTop: "0.35rem" }}>
                      <button
                        type="button"
                        className="btn btn--sm btn--tg-secondary"
                        onClick={() => setLabourTimelinePhaseIds(sortedTimelinePhases.map((p) => String(p.id)))}
                      >
                        Select all timeline levels
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--tg-secondary"
                        onClick={() => setLabourTimelinePhaseIds([])}
                      >
                        Clear all
                      </button>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
                      Selected: <strong>{labourTimelinePhaseIds.length}</strong> timeline level(s). Beneficiaries must
                      have <strong>Approved</strong> status on all selected levels.
                    </p>
                    <div className="issue-po-beneficiary-table-wrap">
                      <table className="data-table issue-po-beneficiary-table">
                        <thead>
                          <tr>
                            <th style={{ width: "2.5rem" }}>
                              <input
                                type="checkbox"
                                checked={
                                  sortedTimelinePhases.length > 0 &&
                                  sortedTimelinePhases.every((p) => labourTimelinePhaseIds.includes(String(p.id)))
                                }
                                onChange={(e) =>
                                  setLabourTimelinePhaseIds(
                                    e.target.checked ? sortedTimelinePhases.map((p) => String(p.id)) : [],
                                  )
                                }
                              />
                            </th>
                            <th>Timeline level</th>
                            <th>Planned days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedTimelinePhases.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-muted">
                                No timeline levels in master yet.
                              </td>
                            </tr>
                          ) : (
                            sortedTimelinePhases.map((phase) => {
                              const phaseId = String(phase.id);
                              return (
                                <tr key={phase.id}>
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={labourTimelinePhaseIds.includes(phaseId)}
                                      onChange={() =>
                                        setLabourTimelinePhaseIds((prev) =>
                                          prev.includes(phaseId)
                                            ? prev.filter((id) => id !== phaseId)
                                            : [...prev, phaseId],
                                        )
                                      }
                                      aria-label={`Select timeline ${phase.title}`}
                                    />
                                  </td>
                                  <td>{phase.title}</td>
                                  <td className="mono">~{phase.approxDays} d</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {(labourBrowseMode === "milestone" && milestoneTemplateId) ||
                (labourBrowseMode === "timeline" && labourTimelinePhaseIds.length > 0) ? (
                  <div style={{ marginTop: "1rem" }}>
                    <span className="inv-outward-issue__label">Completed beneficiaries</span>
                    {linesLoading ? (
                      <p className="text-muted" style={{ marginTop: "0.35rem" }}>
                        Loading beneficiaries…
                      </p>
                    ) : labourBeneficiaries.length === 0 ? (
                      <p className="text-muted" style={{ marginTop: "0.35rem" }}>
                        No beneficiaries have completed{" "}
                        {labourBrowseMode === "milestone"
                          ? selectedSubcontractorMilestone?.title ?? "this milestone"
                          : "all selected timeline levels (office-approved)"}
                        .
                      </p>
                    ) : (
                      <>
                        <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.35rem" }}>
                          Completed: <strong>{labourBeneficiaries.length}</strong> · Ready to invoice:{" "}
                          <strong>{labourBeneficiaries.filter((b) => b.invoiceEligible).length}</strong>
                        </p>
                        <div className="issue-po-beneficiary-table-wrap">
                          <table className="data-table issue-po-beneficiary-table">
                            <thead>
                              <tr>
                                <th>Application ID</th>
                                <th>Name</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {labourBeneficiaries.map((b) => (
                                <tr key={b.applicationId}>
                                  <td className="mono">{b.beneficiaryMobileApplicationId || b.applicationId}</td>
                                  <td>{b.beneficiaryName}</td>
                                  <td>
                                    {b.invoiceEligible ? (
                                      <span style={{ color: "#15803d" }}>Ready to invoice</span>
                                    ) : (
                                      <span className="text-muted">Already invoiced</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            ) : null}
          </section>

          <section className="issue-po-flow__section">
            {invoiceType === "material" ? (
              <>
                <span className="inv-outward-issue__label">Step 3 — Invoice mode</span>
                {!materialPoId ? (
                  <p className="text-muted" style={{ marginTop: "0.5rem" }}>
                    Select a PO above to choose individual or consolidated invoice.
                  </p>
                ) : (
                  <>
                    <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
                      <button
                        type="button"
                        className={`btn btn--sm ${materialInvoiceMode === "individual" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setMaterialInvoiceMode("individual")}
                      >
                        Individual invoices
                      </button>
                      <button
                        type="button"
                        className={`btn btn--sm ${materialInvoiceMode === "consolidated" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setMaterialInvoiceMode("consolidated")}
                      >
                        Single consolidated invoice
                      </button>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
                      {materialInvoiceMode === "individual" ? (
                        <>
                          Creates <strong>{materialBeneficiaryIds.length || selectedMaterialPo?.beneficiaryCount || 0}</strong>{" "}
                          separate invoice(s) — Bill To filled with each beneficiary&apos;s name and site address.
                        </>
                      ) : (
                        <>
                          Creates one invoice for all beneficiaries on this PO — beneficiary breakdown appears in the
                          annexure (current format).
                        </>
                      )}
                    </p>
                  </>
                )}
              </>
            ) : invoiceType === "subcontractor" ? (
              <>
                <span className="inv-outward-issue__label">Step 3 — Invoice mode</span>
                {!subcontractorSelectionReady ? (
                  <p className="text-muted" style={{ marginTop: "0.5rem" }}>
                    Select a milestone or at least one timeline level above to choose individual or consolidated invoice.
                  </p>
                ) : (
                  <>
                    <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
                      <button
                        type="button"
                        className={`btn btn--sm ${subcontractorInvoiceMode === "individual" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setSubcontractorInvoiceMode("individual")}
                      >
                        Individual invoices
                      </button>
                      <button
                        type="button"
                        className={`btn btn--sm ${subcontractorInvoiceMode === "consolidated" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setSubcontractorInvoiceMode("consolidated")}
                      >
                        Single consolidated invoice
                      </button>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
                      {subcontractorInvoiceMode === "individual" ? (
                        <>
                          Creates <strong>{subcontractorBeneficiaryIds.length || invoiceApplicationIds.length}</strong>{" "}
                          separate invoice(s) for beneficiaries ready to invoice.
                        </>
                      ) : (
                        <>
                          Creates one invoice for all beneficiaries ready to invoice on{" "}
                          <strong>
                            {subcontractorBrowseMode === "milestone"
                              ? selectedSubcontractorMilestone?.title ?? "this milestone"
                              : `${subcontractorTimelinePhaseIds.length} timeline level(s)`}
                          </strong>
                          .
                        </>
                      )}
                      {invoiceApplicationIds.length > 1 && !subcontractorValidation.ok ? (
                        <span style={{ color: "#dc2626", display: "block", marginTop: "0.35rem" }}>
                          Eligible beneficiaries must share the same subcontractor.
                        </span>
                      ) : null}
                    </p>
                  </>
                )}
              </>
            ) : invoiceType === "labour" ? (
              <>
                <span className="inv-outward-issue__label">Step 3 — Labour supplier &amp; invoice mode</span>
                <div style={{ marginTop: "0.5rem" }}>
                  <label className="goods-receipts-filters__field" style={{ display: "block", maxWidth: "28rem" }}>
                    <span className="goods-receipts-filters__label">Labour supplier *</span>
                    <SearchableSelect
                      id="inv-labour-supplier"
                      value={labourSupplierId}
                      onChange={setLabourSupplierId}
                      options={labourSupplierOptions}
                    />
                  </label>
                </div>
                {!labourSelectionReady ? (
                  <p className="text-muted" style={{ marginTop: "0.5rem" }}>
                    Select a milestone or at least one timeline level above to choose individual or consolidated invoice.
                  </p>
                ) : (
                  <>
                    <div className="toolbar-row" style={{ marginTop: "0.5rem" }}>
                      <button
                        type="button"
                        className={`btn btn--sm ${labourInvoiceMode === "individual" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setLabourInvoiceMode("individual")}
                      >
                        Individual invoices
                      </button>
                      <button
                        type="button"
                        className={`btn btn--sm ${labourInvoiceMode === "consolidated" ? "btn--primary btn--tg" : "btn--tg-secondary"}`}
                        onClick={() => setLabourInvoiceMode("consolidated")}
                      >
                        Single consolidated invoice
                      </button>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.85rem", marginTop: "0.5rem" }}>
                      {labourInvoiceMode === "individual" ? (
                        <>
                          Creates <strong>{labourBeneficiaryIds.length || invoiceApplicationIds.length}</strong> separate
                          invoice(s) for beneficiaries ready to invoice.
                        </>
                      ) : (
                        <>
                          Creates one invoice for all beneficiaries ready to invoice on{" "}
                          <strong>
                            {labourBrowseMode === "milestone"
                              ? selectedSubcontractorMilestone?.title ?? "this milestone"
                              : `${labourTimelinePhaseIds.length} timeline level(s)`}
                          </strong>
                          .
                        </>
                      )}
                    </p>
                  </>
                )}
              </>
            ) : null}
          </section>

          <section className="issue-po-flow__section">
            <span className="inv-outward-issue__label">Step 4 — Invoice preview lines</span>
            {linesLoading ? <p className="text-muted">Loading eligible lines…</p> : null}
            {invoiceType === "material" && !linesLoading ? (
              !materialPoId ? (
                <p className="text-muted">Select a PO to load material lines.</p>
              ) : materialLines.length === 0 ? (
                <p className="text-muted">
                  No invoiceable lines for this PO. If store inward was recorded, only received quantities can be
                  invoiced; otherwise PO planned quantities are used. Lines already fully invoiced are hidden.
                </p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Beneficiary</th>
                      <th>Item</th>
                      <th>PO</th>
                      <th>Eligible qty</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialLines.map((ln) => {
                      const rawQty = qtyOverrides[ln.lineKey];
                      const qty = rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
                      return (
                        <tr key={ln.lineKey}>
                          <td>{ln.beneficiaryName}</td>
                          <td>{ln.lineLabel}</td>
                          <td className="mono">{ln.poNumber}</td>
                          <td className="mono">{ln.eligibleQty}</td>
                          <td>
                            <input
                              className="form-control"
                              style={{ maxWidth: "6rem" }}
                              value={rawQty ?? String(ln.eligibleQty)}
                              onChange={(e) =>
                                setQtyOverrides((p) => ({ ...p, [ln.lineKey]: sanitizeQtyFieldInput(e.target.value) }))
                              }
                            />
                          </td>
                          <td className="mono">{ln.rate}</td>
                          <td className="mono">{fmtInr(qty * ln.rate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : null}
            {invoiceType === "subcontractor" && !linesLoading ? (
              !subcontractorSelectionReady ? (
                <p className="text-muted">Select a milestone or timeline level to load invoice lines.</p>
              ) : subcontractorLines.length === 0 ? (
                <p className="text-muted">
                  No invoiceable lines — beneficiaries may not have completed subcontractor bill items, items may
                  already be invoiced, or rates are not set in Invoice format.
                </p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Beneficiary</th>
                      <th>Bill line</th>
                      <th>HSN</th>
                      <th>Qty</th>
                      <th>Rate (₹)</th>
                      <th>Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcontractorLines.map((ln) => {
                      const rawRate = rateOverrides[ln.lineKey];
                      const rate =
                        rawRate != null && rawRate.trim() !== "" ? parseQuantityInput(rawRate) : ln.rate;
                      const rawQty = qtyOverrides[ln.lineKey];
                      const qty =
                        rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
                      return (
                        <tr key={ln.lineKey}>
                          <td>{ln.beneficiaryName}</td>
                          <td>{ln.lineLabel}</td>
                          <td className="mono">{ln.hsnCode || "—"}</td>
                          <td>
                            <input
                              className="form-control"
                              style={{ maxWidth: "5rem" }}
                              value={rawQty ?? String(ln.eligibleQty)}
                              onChange={(e) =>
                                setQtyOverrides((p) => ({ ...p, [ln.lineKey]: sanitizeQtyFieldInput(e.target.value) }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-control"
                              style={{ maxWidth: "7rem" }}
                              value={rawRate ?? (ln.rate > 0 ? String(ln.rate) : "")}
                              onChange={(e) =>
                                setRateOverrides((p) => ({ ...p, [ln.lineKey]: sanitizeQtyFieldInput(e.target.value) }))
                              }
                            />
                          </td>
                          <td className="mono">{rate > 0 ? fmtInr(qty * rate) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : null}
            {invoiceType === "labour" && !linesLoading ? (
              !labourSelectionReady ? (
                <p className="text-muted">Select a milestone or timeline level(s) to load labour bill lines.</p>
              ) : labourLines.length === 0 ? (
                <p className="text-muted">
                  No invoiceable labour lines — beneficiaries may not have completed selected timeline levels, items may
                  already be invoiced, or rates are not set in{" "}
                  <Link to="/bills/inward/format">Invoice format → Labour</Link>.
                </p>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Beneficiary</th>
                      <th>Bill line</th>
                      <th>HSN</th>
                      <th>Qty</th>
                      <th>Rate (₹)</th>
                      <th>Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {labourLines.map((ln) => {
                      const rawRate = rateOverrides[ln.lineKey];
                      const rate =
                        rawRate != null && rawRate.trim() !== "" ? parseQuantityInput(rawRate) : ln.rate;
                      const rawQty = qtyOverrides[ln.lineKey];
                      const qty =
                        rawQty != null && rawQty.trim() !== "" ? parseQuantityInput(rawQty) : ln.eligibleQty;
                      return (
                        <tr key={ln.lineKey}>
                          <td>{ln.beneficiaryName}</td>
                          <td>{ln.lineLabel}</td>
                          <td className="mono">{ln.hsnCode || "—"}</td>
                          <td>
                            <input
                              className="form-control"
                              style={{ maxWidth: "5rem" }}
                              value={rawQty ?? String(ln.eligibleQty)}
                              onChange={(e) =>
                                setQtyOverrides((p) => ({ ...p, [ln.lineKey]: sanitizeQtyFieldInput(e.target.value) }))
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-control"
                              style={{ maxWidth: "7rem" }}
                              value={rawRate ?? (ln.rate > 0 ? String(ln.rate) : "")}
                              onChange={(e) =>
                                setRateOverrides((p) => ({ ...p, [ln.lineKey]: sanitizeQtyFieldInput(e.target.value) }))
                              }
                            />
                          </td>
                          <td className="mono">{rate > 0 ? fmtInr(qty * rate) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : null}
          </section>

          <section className="issue-po-flow__section">
            <label className="goods-receipts-filters__field goods-receipts-filters__field--wide">
              <span className="goods-receipts-filters__label">Notes (optional)</span>
              <textarea className="form-control" rows={2} value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} />
            </label>
            <div className="inv-outward-issue__actions">
              <button type="button" className="btn btn--primary btn--tg btn--lg" disabled={generating} onClick={() => void runGenerate()}>
                {generating && generateProgress
                  ? generateProgress.phase === "invoices"
                    ? `Creating invoices ${generateProgress.done}/${generateProgress.total}…`
                    : `Building PDFs ${generateProgress.done}/${generateProgress.total}…`
                  : generating
                    ? "Generating…"
                    : invoiceType === "material" && materialInvoiceMode === "individual" && materialBeneficiaryIds.length > 0
                      ? `Generate ${materialBeneficiaryIds.length} invoices & ZIP`
                      : invoiceType === "material" && materialInvoiceMode === "consolidated"
                        ? "Generate consolidated invoice"
                        : invoiceType === "subcontractor" &&
                            subcontractorInvoiceMode === "individual" &&
                            subcontractorBeneficiaryIds.length > 0
                          ? `Generate ${subcontractorBeneficiaryIds.length} invoices & ZIP`
                          : invoiceType === "labour" &&
                              labourInvoiceMode === "individual" &&
                              labourBeneficiaryIds.length > 0
                            ? `Generate ${labourBeneficiaryIds.length} invoices & ZIP`
                            : "Generate invoice"}
              </button>
            </div>
          </section>
        </div>
      ) : (
        <div className="card card--elevated">
          <div className="goods-receipts-filters__grid" style={{ marginBottom: "0.75rem" }}>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search</span>
              <input className="form-control" value={registerSearch} onChange={(e) => setRegisterSearch(e.target.value)} />
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Type</span>
              <select
                className="form-control"
                value={registerTypeFilter}
                onChange={(e) => setRegisterTypeFilter(e.target.value as "all" | InvoiceType)}
              >
                <option value="all">All types</option>
                <option value="material">Material</option>
                <option value="subcontractor">Subcontractor</option>
                <option value="labour">Labour</option>
              </select>
            </label>
            <div className="goods-receipts-filters__field" style={{ alignSelf: "end" }}>
              <button type="button" className="btn btn--tg-secondary" onClick={() => void loadRegister()}>
                Apply filters
              </button>
            </div>
          </div>
          {registerLoading ? (
            <p className="text-muted">Loading…</p>
          ) : registerRows.length === 0 ? (
            <p className="text-muted">No invoices yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice no.</th>
                  <th>Type</th>
                  <th>Party</th>
                  <th>Beneficiaries</th>
                  <th>Date</th>
                  <th>Grand total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {registerRows.map((row) => (
                  <tr key={row.id}>
                    <td className="mono">{row.invoiceNumber}</td>
                    <td>{invoiceTypeLabel(row.invoiceType)}</td>
                    <td>{row.partyFirmName}</td>
                    <td>{row.beneficiaryCount}</td>
                    <td>{formatWhen(row.createdAt)}</td>
                    <td className="mono">{fmtInr(row.grandTotal)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--tg-secondary"
                        disabled={pdfDownloadingId === row.id}
                        onClick={() => void handleDownloadRegisterPdf(row)}
                      >
                        {pdfDownloadingId === row.id ? "…" : "Download PDF"}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        style={{ marginLeft: "0.35rem" }}
                        disabled={cancellingId === row.id}
                        onClick={() => {
                          if (!window.confirm(`Cancel ${row.invoiceNumber}?`)) return;
                          setCancellingId(row.id);
                          void cancelInvoice(row.id)
                            .then(() => loadRegister())
                            .then(() => showToast("Invoice cancelled.", "success"))
                            .catch((e) => showToast(e instanceof Error ? e.message : "Cancel failed.", "error"))
                            .finally(() => setCancellingId(""));
                        }}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
}
